import { useMemo, type MouseEvent } from "react";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { RoofSelectorProps } from "../types";
import { RoofSvgThumbnail } from "./RoofSvgThumbnail";

export const RoofSelector = ({
  roofs,
  selectedRoofId,
  onSelectRoof,
  onSetOnline,
  onSetOffline,
}: RoofSelectorProps) => {
  // Group roofs by online/offline status
  const { onlineRoofs, offlineRoofs } = useMemo(() => {
    const online = roofs.filter((roof) => roof.isOnline);
    const offline = roofs.filter((roof) => !roof.isOnline);
    return { onlineRoofs: online, offlineRoofs: offline };
  }, [roofs]);

  const handlePowerClick = async (
    event: MouseEvent<HTMLButtonElement>,
    roofId: number,
    isOnline: boolean,
  ) => {
    event.stopPropagation();
    if (isOnline) {
      await onSetOffline(roofId);
    } else {
      await onSetOnline(roofId);
    }
  };

  const renderRoofCard = (roof: (typeof roofs)[0]) => {
    const isSelected = selectedRoofId === roof.roofId;

    return (
      <Paper
        key={roof.roofId}
        onClick={() => onSelectRoof(roof.roofId)}
        elevation={isSelected ? 3 : 1}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: theme.spacing(1, 2),
          cursor: "pointer",
          minWidth: 130,
          transition: "all 0.2s ease",
          border: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : "2px solid transparent",
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : "transparent",
          overflow: "visible",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        <Box sx={{ mb: 0.25 }}>
          <RoofSvgThumbnail roofData={roof} size={90} />
        </Box>

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0 }}>
          {roof.roofName}
        </Typography>

        {/* Show Set Offline button for online roofs */}
        {roof.isOnline ? (
          <Tooltip title="Set Offline">
            <IconButton
              onClick={(e) => handlePowerClick(e, roof.roofId, roof.isOnline)}
              sx={(theme) => ({
                padding: theme.spacing(0.5),
                marginTop: theme.spacing(0.5),
                color: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                },
              })}
            >
              <PowerSettingsNewIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        {/* Show Set Online button for offline roofs */}
        {!roof.isOnline ? (
          <Tooltip title="Set Online">
            <IconButton
              onClick={(e) => handlePowerClick(e, roof.roofId, roof.isOnline)}
              sx={(theme) => ({
                padding: theme.spacing(0.5),
                marginTop: theme.spacing(0.5),
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                },
              })}
            >
              <PowerSettingsNewIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Paper>
    );
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflowX: "auto",
      })}
    >
      {/* Online Roofs Section - Left */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            py: 0.5,
            color: (theme) => theme.palette.success.dark,
          }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Online
          </Typography>
        </Box>
        <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
          {onlineRoofs.map(renderRoofCard)}
        </Box>
      </Box>

      {/* Offline Roofs Section - Right (pushed by space-between) */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            py: 0.5,
            color: "text.secondary",
          }}
        >
          <CancelIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Offline
          </Typography>
        </Box>
        <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
          {offlineRoofs.map(renderRoofCard)}
        </Box>
      </Box>
    </Box>
  );
};
